import React from 'react';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon, 
  ArrowRightIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import Projects from './components/Projects';
import Team from './components/Team';

function App() {
  const currentYear = new Date().getFullYear();

  return (
    <div className="App font-sans text-gray-900">
      {/* 1. Navigation Bar */}
      <Navbar logo="/logo.png" />

      <main>
        {/* 2. Hero / About Section */}
        <section id="about">
          <Hero 
            title="MEK Development Consultants" 
            tagline="Diagnose | Co-Innovate | Transform" 
          />
        </section>

        {/* 3. Services Section */}
        <section id="services" className="py-12 bg-white">
          <Services />
        </section>

        {/* 4. Projects Section */}
        <section id="projects" className="py-12 bg-gray-50">
          <Projects />
        </section>

        {/* 5. Team Section */}
        <section id="team" className="py-12 bg-white">
          <Team />
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-[#1e3a8a] text-white pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16 text-left">
            
            {/* 1. Brand & Mission */}
            <div className="col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-white p-2 rounded-lg">
                  <img src="/logo.png" alt="MEK" className="h-8 w-auto" />
                </div>
                <h3 className="text-xl font-black tracking-tighter">MEK</h3>
              </div>
              <p className="text-blue-100 text-sm leading-relaxed mb-6">
                A hub for excellence in engineering and sustainable rural development. 
                Delivering quality control and value for money in Malawi and beyond.
              </p>
            </div>

            {/* 2. Navigation Quick Links */}
            <div>
              <h4 className="text-[#22c55e] font-bold uppercase text-[10px] tracking-[0.2em] mb-8">Menu</h4>
              <ul className="space-y-4">
                {[
                  { label: 'About Us',  href: '#about'    },
                  { label: 'Services',  href: '#services' },
                  { label: 'Projects',  href: '#projects' },
                  { label: 'Our Team',  href: '#team'     },
                ].map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="text-sm text-blue-100 hover:text-white transition-all flex items-center gap-2 group"
                    >
                      <ArrowRightIcon className="w-3 h-3 text-[#22c55e]" />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* 3. Contact Info — two emails */}
            <div>
              <h4 className="text-[#22c55e] font-bold uppercase text-[10px] tracking-[0.2em] mb-8">Lilongwe Office</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPinIcon className="w-5 h-5 text-[#22c55e] shrink-0 mt-0.5" />
                  <span className="text-sm text-blue-100">P.O. Box 143, Lilongwe, Malawi</span>
                </li>
                <li className="flex items-center gap-3">
                  <PhoneIcon className="w-5 h-5 text-[#22c55e] shrink-0" />
                  <span className="text-sm text-blue-100">(+265) 995 028 207</span>
                </li>
                <li className="flex items-start gap-3">
                  <EnvelopeIcon className="w-5 h-5 text-[#22c55e] shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-1">
                    <a href="mailto:kmikwamba@gmail.com" className="text-sm text-blue-100 hover:text-white transition-colors">
                      kmikwamba@gmail.com
                    </a>
                    <a href="mailto:info@mekconsultantsmw.com" className="text-sm text-blue-100 hover:text-white transition-colors">
                      info@mekconsultantsmw.com
                    </a>
                  </div>
                </li>
              </ul>
            </div>

            {/* 4. Standards — with NCIC logo */}
            <div>
              <h4 className="text-[#22c55e] font-bold uppercase text-[10px] tracking-[0.2em] mb-8">Standards</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-blue-100">
                  <GlobeAltIcon className="w-5 h-5 text-[#22c55e]" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">World Bank Standards</span>
                </div>
                <div className="flex items-center gap-3 text-blue-100">
                  <GlobeAltIcon className="w-5 h-5 text-[#22c55e]" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">AfDB Guidelines</span>
                </div>
                <div className="mt-6 pt-6 border-t border-white/10">
                  {/* NCIC logo + text */}
                  <div className="flex items-center gap-3">
                    <div className="bg-white rounded-lg p-1.5 flex-shrink-0">
                      <img src="/ncic-logo.jpg" alt="NCIC" className="h-8 w-auto object-contain" />
                    </div>
                    <p className="text-[10px] text-blue-300 font-medium italic leading-relaxed">
                      Registered with the National Construction Industry Council (NCIC).
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom Copyright Bar */}
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest">
              © {currentYear} MEK Development Consultants. All Rights Reserved.
            </p>
            <div className="flex gap-6 text-[9px] font-black uppercase tracking-[0.3em]">
              <span className="text-[#22c55e]">Sustainability</span>
              <span className="text-[#22c55e]">Excellence</span>
              <span className="text-[#22c55e]">Integrity</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;